import { Add, RequestQuoteOutlined } from "@mui/icons-material";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  CommercialSummaryCards,
  ProposalDetailsDialog,
  ProposalFormDialog,
  SavedProposalsCard,
} from "./components";
import { DEFAULT_PROPOSAL_STATUS } from "./constants/proposalStatuses";
import { useProposal } from "./hooks/useProposal";
import { LocalStorageProposalRepository } from "./repositories/LocalStorageProposalRepository";
import {
  ProposalOperationalDetails,
  ProposalStatus,
  SavedProposal,
} from "./types/proposal";
import { generateNotionNote } from "./utils/notionNote";
import { createProposalId } from "./utils/proposalId";
import { copyToClipboard } from "./utils/clipboard";
import { createEmptyOperationalDetails } from "./utils/proposalOperationalDetails";

type Feedback = {
  open: boolean;
  message: string;
  severity: AlertColor;
};

type CommercialPageProps = {
  user?: {
    id?: string | number;
  };
};

const CommercialPage: React.FC<CommercialPageProps> = ({ user }) => {
  const proposal = useProposal();
  const proposalRepository = useMemo(
    () => new LocalStorageProposalRepository(String(user?.id ?? "admin")),
    [user?.id],
  );
  const [feedback, setFeedback] = useState<Feedback>({
    open: false,
    message: "",
    severity: "success",
  });
  const [savedProposals, setSavedProposals] = useState<SavedProposal[]>([]);
  const [currentProposalId, setCurrentProposalId] = useState<string | null>(null);
  const [proposalStatus, setProposalStatus] = useState<ProposalStatus>(
    DEFAULT_PROPOSAL_STATUS,
  );
  const [detailsProposal, setDetailsProposal] = useState<SavedProposal | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    if (window.setPageTitle) window.setPageTitle("Comercial - Admin");
  }, []);

  const showFeedback = useCallback(
    (message: string, severity: "success" | "error" | "info") => {
      setFeedback({ open: true, message, severity });
    },
    [],
  );

  const refreshSavedProposals = useCallback(async () => {
    try {
      setSavedProposals(await proposalRepository.list());
    } catch {
      showFeedback(
        "Não foi possível acessar as propostas salvas neste navegador.",
        "error",
      );
    }
  }, [proposalRepository, showFeedback]);

  useEffect(() => {
    void refreshSavedProposals();
  }, [refreshSavedProposals]);

  const handleCalculateFreight = useCallback(() => {
    // TODO: integrar com Correios ou Melhor Envio.
    showFeedback(
      "O cálculo automático será integrado em breve. Informe o frete manualmente.",
      "info",
    );
  }, [showFeedback]);

  const handleNewProposal = useCallback(() => {
    proposal.resetProposal();
    setCurrentProposalId(null);
    setProposalStatus(DEFAULT_PROPOSAL_STATUS);
    setFormOpen(true);
  }, [proposal]);

  const hasSelectedProducts = proposal.products.some(product => product.selected);
  const canSave = !!proposal.client.name.trim() && hasSelectedProducts;

  const handleSaveProposal = useCallback(async () => {
    if (!proposal.client.name.trim()) {
      showFeedback("Informe o nome do cliente antes de salvar.", "error");
      return;
    }
    if (!hasSelectedProducts) {
      showFeedback("Selecione pelo menos um produto antes de salvar.", "error");
      return;
    }

    const now = new Date().toISOString();
    const existingProposal = savedProposals.find(
      savedProposal => savedProposal.id === currentProposalId,
    );
    const savedProposal: SavedProposal = {
      id: currentProposalId ?? createProposalId(),
      status: proposalStatus,
      createdAt: existingProposal?.createdAt ?? now,
      updatedAt: now,
      draft: proposal.draft,
      totals: proposal.totals,
      operationalDetails:
        existingProposal?.operationalDetails ??
        createEmptyOperationalDetails(proposal.client),
    };

    try {
      await proposalRepository.save(savedProposal);
      setCurrentProposalId(savedProposal.id);
      await refreshSavedProposals();
      setFormOpen(false);
      showFeedback(
        existingProposal
          ? "Proposta atualizada com sucesso."
          : "Proposta salva neste navegador.",
        "success",
      );
    } catch {
      showFeedback("Não foi possível salvar a proposta.", "error");
    }
  }, [
    currentProposalId,
    hasSelectedProducts,
    proposal.client.name,
    proposal.draft,
    proposal.totals,
    proposalStatus,
    proposalRepository,
    refreshSavedProposals,
    savedProposals,
    showFeedback,
  ]);

  const copyNotionNote = useCallback(
    async (savedProposal: SavedProposal) => {
      try {
        await copyToClipboard(generateNotionNote(savedProposal));
        showFeedback("Anotação copiada. Agora é só colar no Notion.", "success");
      } catch {
        showFeedback("Não foi possível copiar a anotação.", "error");
      }
    },
    [showFeedback],
  );

  const handleCopyCurrentNotion = useCallback(() => {
    const savedProposal = savedProposals.find(
      proposalItem => proposalItem.id === currentProposalId,
    );
    if (!savedProposal) {
      showFeedback("Salve a proposta antes de copiar para o Notion.", "info");
      return;
    }

    void copyNotionNote({
      ...savedProposal,
      status: proposalStatus,
      updatedAt: new Date().toISOString(),
      draft: proposal.draft,
      totals: proposal.totals,
    });
  }, [
    copyNotionNote,
    currentProposalId,
    proposal.draft,
    proposal.totals,
    proposalStatus,
    savedProposals,
    showFeedback,
  ]);

  const handleOpenProposal = useCallback(
    (savedProposal: SavedProposal) => {
      proposal.loadProposal(savedProposal.draft);
      setCurrentProposalId(savedProposal.id);
      setProposalStatus(savedProposal.status);
      setFormOpen(true);
    },
    [proposal],
  );

  const handleSavedStatusChange = useCallback(
    async (savedProposal: SavedProposal, status: ProposalStatus) => {
      try {
        const updatedProposal = {
          ...savedProposal,
          status,
          updatedAt: new Date().toISOString(),
        };
        await proposalRepository.save(updatedProposal);
        if (currentProposalId === savedProposal.id) setProposalStatus(status);
        if (detailsProposal?.id === savedProposal.id) {
          setDetailsProposal(updatedProposal);
        }
        await refreshSavedProposals();
        showFeedback("Status atualizado.", "success");
      } catch {
        showFeedback("Não foi possível atualizar o status.", "error");
      }
    },
    [
      currentProposalId,
      detailsProposal?.id,
      proposalRepository,
      refreshSavedProposals,
      showFeedback,
    ],
  );

  const handleSaveProposalDetails = useCallback(
    async (
      savedProposal: SavedProposal,
      operationalDetails: ProposalOperationalDetails,
      status: ProposalStatus,
    ) => {
      const updatedProposal: SavedProposal = {
        ...savedProposal,
        operationalDetails,
        status,
        updatedAt: new Date().toISOString(),
      };

      try {
        await proposalRepository.save(updatedProposal);
        setDetailsProposal(updatedProposal);
        if (currentProposalId === updatedProposal.id) setProposalStatus(status);
        await refreshSavedProposals();
        showFeedback("Detalhes da proposta atualizados.", "success");
      } catch {
        showFeedback("Não foi possível salvar os detalhes.", "error");
      }
    },
    [currentProposalId, proposalRepository, refreshSavedProposals, showFeedback],
  );

  const handleDeleteProposal = useCallback(
    async (savedProposal: SavedProposal) => {
      const confirmation = await Swal.fire({
        title: "Excluir proposta?",
        text: `${savedProposal.id} será removida somente deste navegador.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#d32f2f",
      });
      if (!confirmation.isConfirmed) return;

      try {
        await proposalRepository.remove(savedProposal.id);
        if (currentProposalId === savedProposal.id) {
          proposal.resetProposal();
          setCurrentProposalId(null);
          setProposalStatus(DEFAULT_PROPOSAL_STATUS);
          setFormOpen(false);
        }
        if (detailsProposal?.id === savedProposal.id) setDetailsProposal(null);
        await refreshSavedProposals();
        showFeedback("Proposta excluída.", "success");
      } catch {
        showFeedback("Não foi possível excluir a proposta.", "error");
      }
    },
    [
      currentProposalId,
      detailsProposal?.id,
      proposal,
      proposalRepository,
      refreshSavedProposals,
      showFeedback,
    ],
  );

  return (
    <Box sx={{ py: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <RequestQuoteOutlined />
          </Box>
          <div>
            <Typography variant="h4" component="h1">
              Comercial
            </Typography>
            <Typography color="text.secondary">
              Propostas, pagamentos e pedidos em um só lugar.
            </Typography>
          </div>
        </Stack>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleNewProposal}
          sx={{ minHeight: 44 }}
        >
          Nova proposta
        </Button>
      </Stack>

      <CommercialSummaryCards proposals={savedProposals} />

      <SavedProposalsCard
        proposals={savedProposals}
        activeProposalId={formOpen ? currentProposalId : null}
        onView={setDetailsProposal}
        onOpen={handleOpenProposal}
        onStatusChange={(savedProposal, status) =>
          void handleSavedStatusChange(savedProposal, status)
        }
        onCopyNotion={savedProposal => void copyNotionNote(savedProposal)}
        onDelete={savedProposal => void handleDeleteProposal(savedProposal)}
      />

      <ProposalFormDialog
        open={formOpen}
        proposal={proposal}
        proposalId={currentProposalId}
        status={proposalStatus}
        canSave={canSave}
        onClose={() => setFormOpen(false)}
        onStatusChange={setProposalStatus}
        onSave={() => void handleSaveProposal()}
        onCopyNotion={handleCopyCurrentNotion}
        onCalculateFreight={handleCalculateFreight}
        onFeedback={showFeedback}
      />

      <ProposalDetailsDialog
        proposal={detailsProposal}
        onClose={() => setDetailsProposal(null)}
        onSave={(savedProposal, operationalDetails, status) =>
          void handleSaveProposalDetails(savedProposal, operationalDetails, status)
        }
      />

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback(current => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() => setFeedback(current => ({ ...current, open: false }))}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommercialPage;
